import { useTranslation } from 'react-i18next';
import { useGetActionPlanAggregatedQuery } from '../../../../hooks/queries/useDashboardPublicQueries';
import { Button } from '../../../../components/Button';
import { ArrowRightIcon } from '@navikt/aksel-icons';
import { TeamExpectations } from '../TeamExpectations';
import { capitalizeFirstLetter, extractFirstSentence } from '../../../../utils/stringUtils';
import classes from './ActionPlanSummaryCard.module.css';

const SUMMARY_CARD_MAX_ITEMS = 3;

interface ActionPlanSummaryCardProps {
  surveyUuid: string;
  onViewFullReport: () => void;
}

export const ActionPlanSummaryCard = ({
  surveyUuid,
  onViewFullReport,
}: ActionPlanSummaryCardProps) => {
  const { t } = useTranslation();
  const { data: aggregated, isLoading } = useGetActionPlanAggregatedQuery(surveyUuid);

  if (isLoading || !aggregated) {
    return null;
  }

  const commitments = aggregated.pairedItems
    .map(item => item.commitment)
    .filter((commitment): commitment is { statement: string } => Boolean(commitment?.statement))
    .map(commitment => ({
      statement: capitalizeFirstLetter(commitment.statement),
    }));

  const expectations = aggregated.pairedItems
    .map(item => item.expectation)
    .filter((expectation): expectation is { statement: string } => Boolean(expectation?.statement))
    .map(expectation => ({
      statement: capitalizeFirstLetter(expectation.statement),
    }));

  const keyInsight = aggregated.interpretiveSummary
    ? extractFirstSentence(aggregated.interpretiveSummary).trim()
    : null;

  const hasBaseline = aggregated.baseline;
  const scoreChange = hasBaseline ? aggregated.baseline.scoreChange : null;
  const scoreTrend = hasBaseline ? aggregated.baseline.scoreTrend : null;

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.badge}>
            {t('publicSurvey.actionPlanSummary.badge')}
          </div>
          {hasBaseline && scoreChange !== null && (
            <div className={`${classes.trend} ${classes[scoreTrend || 'stable']}`}>
              {scoreTrend === 'up' && '↑'}
              {scoreTrend === 'down' && '↓'}
              {scoreTrend === 'stable' && '→'}
              {scoreChange >= 0 ? '+' : ''}{scoreChange.toFixed(1)}
            </div>
          )}
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.focusArea}>
          <h3 className={classes.focusLabel}>
            {t('publicSurvey.actionPlanSummary.focusAreaLabel')}
          </h3>
          <div className={classes.focusTitle}>
            {aggregated.categoryName}
          </div>
        </div>

        {keyInsight && (
          <p className={classes.insight}>
            {keyInsight}.
          </p>
        )}

        {(commitments.length > 0 || expectations.length > 0) && (
          <TeamExpectations
            commitments={commitments}
            expectations={expectations}
            maxItems={SUMMARY_CARD_MAX_ITEMS}
            showFooter={true}
          />
        )}
      </div>

      <div className={classes.footer}>
        <Button
          variant="primary"
          size="large"
          onClick={onViewFullReport}
          className={classes.button}
        >
          {t('publicSurvey.actionPlanSummary.viewFullReport')}
          <ArrowRightIcon fontSize="1.25rem" />
        </Button>
      </div>
    </div>
  );
};
